import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface DigitalesPesv {
  id: number;
  siglas: string;
  tipo_documentos: string;
  formatos: string;
  url?: string;
  id_relacion?: number;
  // opcional: relación con nombre_documento
  nombre_documento_id?: number;
  estado: number;
  fecharegistro: string;
  fechamodificacion: string;
}

@Injectable({
  providedIn: 'root',
})
export class DigitalesPesvService {
  private apiUrl = `${environment.apiUrl}/api/digitales-pesv`;
  private documentosUrl = `${environment.apiUrl}/api/documentos`;
  private readonly requestTimeoutMs = 45000;

  constructor(private http: HttpClient) {}

  getAll(): Observable<DigitalesPesv[]> {
    return this.http
      .get<DigitalesPesv[]>(this.apiUrl, { withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  getById(id: number): Observable<DigitalesPesv> {
    return this.http
      .get<DigitalesPesv>(`${this.apiUrl}/${id}`, { withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  getByPunto(idRelacion: number): Observable<DigitalesPesv | null> {
    return this.getAll().pipe(
      map((items: DigitalesPesv[]) => {
        const lista = items || [];
        const activos = lista.filter((d) => Number(d.estado) === 1);
        return (
          activos.find((d) => Number(d.id_relacion) === Number(idRelacion)) ||
          lista.find((d) => Number(d.id_relacion) === Number(idRelacion)) ||
          null
        );
      }),
    );
  }

  /**
   * Obtiene el registro de digitales PESV asociado a un nombre_documento concreto.
   * Requiere un endpoint en backend: GET /api/digitales-pesv/by-nombre-documento/:id
   */
  getByNombreDocumento(nombreDocumentoId: number): Observable<DigitalesPesv> {
    return this.http
      .get<DigitalesPesv>(`${this.apiUrl}/by-nombre-documento/${nombreDocumentoId}`, {
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  create(data: Partial<DigitalesPesv>): Observable<DigitalesPesv> {
    return this.http
      .post<DigitalesPesv>(this.apiUrl, data, { withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  update(id: number, data: Partial<DigitalesPesv>): Observable<DigitalesPesv> {
    return this.http
      .put<DigitalesPesv>(`${this.apiUrl}/${id}`, data, { withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  inactivate(id: number): Observable<any> {
    return this.http
      .patch(
        `${this.apiUrl}/${id}/inactivate`,
        {},
        { withCredentials: true },
      )
      .pipe(catchError(this.handleError));
  }

  delete(id: number): Observable<any> {
    return this.http
      .delete(`${this.apiUrl}/${id}`, { withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  /**
   * 📥 Descargar el documento digital PESV más reciente desde la ruta del backend
   * Ruta: /api/digitales-pesv/:empresaNit/:siglas
   */
  descargarArchivoDigitalesPesv(
    nit: string,
    siglas: string,
  ): Observable<any> {
    const url = `${this.apiUrl}/${encodeURIComponent(nit)}/${encodeURIComponent(siglas)}`;
    return this.http.get(url, {
      withCredentials: true,
      responseType: 'blob',
      observe: 'response',
    }).pipe(timeout(this.requestTimeoutMs));
  }

  /**
   * 📤 Subir documento digital PESV usando el contrato del backend
   * Ruta: POST /api/digitales-pesv
   */
  subirArchivoDigitalesPesv(
    file: File,
    nit: string,
    siglas: string,
    nombreArchivo?: string
  ): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('empresaNit', nit);
    formData.append('siglas', siglas);
    if (nombreArchivo) {
      formData.append('nombreArchivo', nombreArchivo);
    }

    const url = this.apiUrl;

    return this.http.post<any>(url, formData, {
      reportProgress: true,
      withCredentials: true,
      observe: 'events',
    }).pipe(catchError(this.handleError));
  }

  /**
   * 📋 Listar archivos de DigitalesPesv desde la ruta con NIT administrativo
   * Ruta: {NIT_ADMIN}/pesv/{siglas}/
   */
  listarArchivosDigitalesPesv(nit: string, siglas: string): Observable<any> {
    const carpeta = `${nit}/pesv/${siglas}`;
    const carpetaCodificada = encodeURIComponent(carpeta);
    const url = `${this.documentosUrl}/${carpetaCodificada}/listar`;
    return this.http.get(url, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: any) {
    console.error('Error en DigitalesPesvService:', error);
    const message = error?.error?.message || error.message || 'Error en la solicitud.';
    return throwError(() => new Error(message));
  }
}
